import { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from './ui.tsx';
import { dismissSendOutcome, useSendOutcome, useSendOutcomeQueueSize } from '../hooks/use-send-outcome.ts';
import { usePdfDownloadStatus } from '../hooks/use-pdf-download.ts';

/** Matches `bottom-4` — the offset either indicator sits at alone. */
const BASE_OFFSET = 16;
/** Space left between the two when both are stacked. */
const STACK_GAP = 8;

/**
 * The page-level echo of the last quote sent from a list row.
 *
 * Mounted once for the whole app, the same reason `PdfDownloadIndicator` is:
 * the row a send started from can be gone — refetched out of a filtered
 * view, sorted off the page — by the time the result is ready to read, so the
 * message has to live somewhere that outlives it. `Alert` already maps tone
 * to the right ARIA role (`alert` for an error, `status` otherwise), which is
 * what lets a screen reader notice this without the reader having had focus
 * on the button that is gone.
 *
 * Sending from more than one row before either finishes is why this reads a
 * queue rather than a single slot (see `use-send-outcome.ts`): a failure on
 * one quote must not be silently replaced by a success on another before
 * anyone has read it. Dismissing the one on screen reveals the next.
 *
 * Both indicators anchor to the same corner, so a send outcome left up while
 * a PDF starts downloading would sit directly on top of it. This measures
 * `PdfDownloadIndicator`'s actual rendered height — via the id it exposes for
 * exactly this — rather than assuming one: that box's own height changes with
 * its error text and the viewport's width, and a fixed guess is wrong the
 * moment either does.
 */
export function SendOutcomeIndicator() {
  const { t } = useTranslation();
  const outcome = useSendOutcome();
  const queueSize = useSendOutcomeQueueSize();
  const pdfStatus = usePdfDownloadStatus();
  const pdfIndicatorShowing = pdfStatus.active > 0 || Boolean(pdfStatus.error);
  const [offset, setOffset] = useState(BASE_OFFSET);

  useLayoutEffect(() => {
    if (!pdfIndicatorShowing) {
      setOffset(BASE_OFFSET);
      return;
    }
    const measure = () => {
      const el = document.getElementById('pdf-download-indicator');
      setOffset(el ? el.getBoundingClientRect().height + BASE_OFFSET + STACK_GAP : BASE_OFFSET);
    };
    measure();
    // The PDF indicator's own text — and so its height — can change without
    // `pdfStatus` doing so (a narrower window wrapping the same words), so
    // this re-measures on resize too rather than only when this effect reruns.
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [pdfIndicatorShowing, pdfStatus.active, pdfStatus.error]);

  if (!outcome) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-50 flex justify-center px-4 print:hidden"
      // Above the phone's bottom navigation bar, where there is one (index.css).
      style={{ bottom: `calc(var(--shell-bottom-nav, 0px) + ${offset}px)` }}
    >
      <div className="pointer-events-auto w-full max-w-md">
        <Alert tone={outcome.tone}>
          <div className="flex items-start justify-between gap-3">
            <span>
              {outcome.text}
              {queueSize > 1 ? (
                <span className="mt-1 block text-xs text-[color:var(--color-muted)]">
                  {t('common.sendOutcomeMore', { count: queueSize - 1 })}
                </span>
              ) : null}
            </span>
            <Button variant="ghost" onClick={dismissSendOutcome}>
              {t('common.dismiss')}
            </Button>
          </div>
        </Alert>
      </div>
    </div>
  );
}
